import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ReportStateBadge } from "@/components/ReportStateBadge";
import {
  currentPeriod,
  useObligations,
  usePeriods,
  useProjects,
  useReport,
  useRevisionRequests,
} from "@/features/report/queries";
import type { Obligation } from "@/features/report/types";
import { useRecentReports } from "@/features/reports/queries";
import { useTimezone } from "@/features/calendar/queries";
import { formatInstant, formatLocalDate, todayLocal } from "@/lib/dates";

/** UI-02: what is owed this week, when it is due, and one way into the weekly flow. */
export function StudentHomePage() {
  const { t } = useTranslation();
  const timezone = useTimezone();
  const periods = usePeriods();
  const period = currentPeriod(periods.data, new Date(), timezone);
  const obligations = useObligations(period?.id);
  const projects = useProjects();
  const report = useReport(period?.id);
  const revisions = useRevisionRequests(report.data?.id);

  if (periods.isPending) return <p className="stamp">{t("common.loading")}</p>;
  if (!period) return <p className="text-sm text-muted-foreground">{t("me.noPeriod")}</p>;

  const titleOf = (projectId: string) =>
    projects.data?.items.find((project) => project.id === projectId)?.title ?? projectId;

  // The headline state is about the *package*, not about the report row. "Submitted" says only
  // that something was handed in, so a week submitted on Monday still read "Submitted" after
  // Wednesday's new project added an entry nobody has written — and gave the student no reason to
  // reopen the week. REP-08 calls that obligation unfulfilled, and so does this screen now.
  //
  // Only over the two states that mean "something is in". A draft with nothing submitted is a
  // draft, and "Revision requested" is the professor waiting on something specific, which a count
  // of projects would bury.
  const owed = (obligations.data ?? []).filter(
    (obligation) => obligation.state === "required" && !obligation.submitted,
  ).length;
  // What the professor asked for, per project: it is the one thing on this screen that is not
  // finished, and "Submitted" beside the project it was asked about read as the opposite.
  const open = (revisions.data ?? []).filter((request) => !request.resolved_in_version_id);
  const askedOf = (projectId: string) =>
    open.filter((request) => String(request.project_id) === projectId);
  const workflowState = report.data?.workflow_state ?? "not_started";
  const partly = owed > 0 && (workflowState === "submitted" || workflowState === "resubmitted");
  const state = report.isPending
    ? null
    : partly
      ? t("me.partlySubmitted", { count: owed })
      : t(`report.state.${workflowState}`);

  return (
    <section className="max-w-2xl animate-rise-in">
      <header>
        <p className="eyebrow mb-1.5">
          {formatLocalDate(period.local_start)} – {formatLocalDate(period.local_end)}
        </p>
        <h1 className="page-title">{t("me.title")}</h1>
      </header>

      {/* The deadline is the point of this screen, so it is set as a figure rather than a caption. */}
      <div className="panel mt-6 flex flex-wrap items-center justify-between gap-5 p-5">
        <div>
          <p className="eyebrow">{t("me.dueBy")}</p>
          <p className="mt-1.5 font-display text-display leading-none" data-testid="next-deadline">
            {formatInstant(period.deadline_utc, timezone)}
          </p>
          {/* Held back until the report has answered. Defaulting to `not_started` meant a
              submitted week painted briefly as an untouched one, under a deadline that was
              already correct — so the panel looked settled while it was still wrong. */}
          <p
            className={
              partly
                ? "mt-2 font-mono text-xs text-warn"
                : "mt-2 font-mono text-xs text-muted-foreground"
            }
            data-testid="report-state"
          >
            {/* A non-breaking space, so the panel keeps its height while the state is unknown. */}
            {state ?? <>&nbsp;</>}
          </p>
          {/* Where the professor's reason actually is. Until the reader existed, a week marked
              "revision requested" said so and nowhere in the app said what to change — the reason
              travelled only in an email. */}
          {report.data?.current_version_id ? (
            <Link
              to={`/report/${period.id}/submitted`}
              className="link mt-2 inline-block font-mono text-xs"
              data-testid="read-submitted"
            >
              {t("report.reader.seeSubmitted")}
            </Link>
          ) : null}
        </div>
        {/* "Open" while the answer is outstanding: it is true either way, where "Start this
            week" on a week already submitted is not. */}
        <Link to={`/report/${period.id}`} className="btn-primary no-underline">
          {open.length > 0
            ? t("me.reviseWeek")
            : report.isPending || report.data
              ? t("me.openWeek")
              : t("me.startWeek")}
        </Link>
      </div>

      {open.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="home-revisions">
          {open.map((request) => (
            <p
              key={request.id}
              className="border-l-[3px] border-l-warn-rule bg-muted px-3.5 py-2.5 text-ui text-warn"
            >
              <span className="font-medium">{titleOf(String(request.project_id))}.</span>{" "}
              {t("me.revisionAsked")} {request.reason}
            </p>
          ))}
        </div>
      )}

      <h2 className="section-title mt-8">{t("me.obligations")}</h2>
      <ul className="panel mt-2.5">
        {obligations.data?.map((obligation) => (
          <li key={obligation.id} className="row">
            {/* The student's own way into UI-03: the project page is signed-in, not prof-only. */}
            <Link to={`/projects/${obligation.project_id}`} className="link">
              {titleOf(obligation.project_id)}
            </Link>
            <ObligationState
              obligation={obligation}
              revisionRequested={askedOf(obligation.project_id).length > 0}
            />
          </li>
        ))}
        {obligations.data?.length === 0 && (
          <li className="px-4 py-2.5 text-sm text-muted-foreground">{t("me.nothingOwed")}</li>
        )}
      </ul>

      {/* The only way into the released record since this screen stopped carrying it, and the
          only inbound link to /me/profile anywhere in the app. */}
      <p className="stamp mt-3">
        <Link to="/me/profile" className="link" data-testid="to-my-progress">
          {t("me.toProfile")}
        </Link>
      </p>

      <PastWeeks currentPeriodId={period.id} timezone={timezone} />
    </section>
  );
}

/**
 * Whether this project is still owed, in the three states a student can actually be in.
 *
 * `state` is `required` or `excused` and says whether the project has to be in the package — it
 * never changes on submission, so rendering it alone left a finished week reading REQUIRED in the
 * warning colour however many times it had been submitted. `submitted` (REP-08) is the other half,
 * and it is computed from the report's current version, so this agrees with the professor's
 * outstanding list by construction.
 */
function ObligationState({
  obligation,
  revisionRequested,
}: {
  obligation: Obligation;
  revisionRequested: boolean;
}) {
  const { t } = useTranslation();
  if (obligation.state === "excused") {
    return (
      <span className="font-mono text-meta text-faint">
        {t("report.obligation.excused")}
        {obligation.excuse_reason ? ` — ${obligation.excuse_reason}` : ""}
      </span>
    );
  }
  if (revisionRequested) {
    return (
      <span className="font-mono text-meta uppercase tracking-[0.06em] text-warn">
        {t("report.state.revision_requested")}
      </span>
    );
  }
  return (
    <span
      className={
        obligation.submitted
          ? "font-mono text-meta uppercase tracking-[0.06em] text-good"
          : "font-mono text-meta uppercase tracking-[0.06em] text-warn"
      }
    >
      {t(obligation.submitted ? "report.obligation.submitted" : "report.obligation.required")}
    </span>
  );
}

/**
 * The weeks before this one, each with what was handed in for it.
 *
 * Merged from the weeks and the reports rather than listing the reports alone: a week with nothing
 * submitted is the one a student most needs to see, and a list of reports cannot show an absence.
 * A submitted week opens what was submitted. A week with nothing submitted opens the editor: it is
 * the student's only way to file a late report or finish a draft for an earlier week.
 */
function PastWeeks({ currentPeriodId, timezone }: { currentPeriodId: string; timezone: string }) {
  const { t } = useTranslation();
  const periods = usePeriods();
  const reports = useRecentReports({});
  const today = todayLocal(new Date(), timezone);

  const past = (periods.data ?? [])
    .filter((one) => one.id !== currentPeriodId && one.local_end < today)
    .sort((a, b) => b.local_start.localeCompare(a.local_start))
    .slice(0, 8);
  const byWeek = new Map((reports.data?.items ?? []).map((row) => [row.period_id, row]));

  // Held until the reports answer, so no week is shown as missing while it is still being asked.
  if (!past.length || !reports.data) return null;

  return (
    <>
      <h2 className="section-title mt-8">{t("me.pastWeeks")}</h2>
      <ul className="panel mt-2.5" data-testid="past-weeks">
        {past.map((one) => {
          const dates = `${formatLocalDate(one.local_start)} – ${formatLocalDate(one.local_end)}`;
          const report = byWeek.get(one.id);
          return (
            <li key={one.id} className="row">
              {report ? (
                <>
                  <Link to={`/report/${one.id}/submitted`} className="link">
                    {dates}
                  </Link>
                  <ReportStateBadge state={report.workflow_state} />
                </>
              ) : (
                <>
                  <Link to={`/report/${one.id}`} className="link">
                    {dates}
                  </Link>
                  <span className="font-mono text-meta text-warn">{t("me.notSubmitted")}</span>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
